import JZZ from 'jzz';
import { createLogger } from './logger';

// ============================================================
// Types
// ============================================================

/**
 * MIDI output interface (subset of JZZ output).
 */
interface MIDIOutput {
  noteOn(channel: number, note: number, velocity: number): void;
  noteOff(channel: number, note: number): void;
  control(channel: number, controller: number, value: number): void;
}

/**
 * JZZ instance interface (subset).
 */
interface JZZInstance {
  openMidiOut(port?: string): MIDIOutput;
  info?: () => {
    outputs?: Array<{ name?: string }>;
  };
}

/**
 * Factory function for creating JZZ instance.
 */
type JZZFactory = () => Promise<JZZInstance>;

// ============================================================
// State
// ============================================================

const log = createLogger('midi');

/** Track scheduled timers so we can cancel future MIDI events on Stop. */
const scheduledTimers = new Set<ReturnType<typeof setTimeout>>();

/** Cached JZZ instance promise. */
let cachedMidi: Promise<JZZInstance> | null = null;

/** Cache of opened MIDI outputs by port name (or '__default__'). */
const outputCache = new Map<string, MIDIOutput>();

/** Allow injecting a mock JZZ factory for testing. */
let jzzFactoryOverride: JZZFactory | null = null;

// ============================================================
// Test Hooks
// ============================================================

/**
 * Test hook to inject a mock JZZ factory.
 */
export function _setJZZFactory(factory: JZZFactory | null): void {
  jzzFactoryOverride = factory;
}

// ============================================================
// MIDI Output Caching
// ============================================================

/**
 * Clear cached MIDI instance and all output ports.
 * Useful for testing and cleanup on stop.
 */
export function clearMidiCache(): void {
  cachedMidi = null;
  outputCache.clear();
}

function resolvePortName(
  midi: JZZInstance,
  port: number | string
): string | null {
  if (typeof port === 'string' && port.trim().length > 0) {
    return port;
  }

  const n = Number(port);
  if (!Number.isFinite(n)) {
    return null;
  }

  const portIndex = Math.floor(n) - 1;
  if (portIndex < 0) {
    return null;
  }

  const outputs = midi.info?.()?.outputs ?? [];
  const selected = outputs[portIndex];
  if (!selected || !selected.name) {
    log.warn('Requested MIDI port %s not found. Falling back to default output.', String(port));
    return null;
  }

  return selected.name;
}

/**
 * Get (or create and cache) a MIDI output for the given port.
 *
 * @param port - Port name (string) or numeric (uses default)
 * @returns MIDI output object
 */
export async function getMidiOutput(port: number | string): Promise<MIDIOutput> {
  // Initialize JZZ once
  if (!cachedMidi) {
    const factory = jzzFactoryOverride || (() => JZZ() as unknown as Promise<JZZInstance>);
    cachedMidi = factory();
  }
  const midi = await cachedMidi;

  const portName = resolvePortName(midi, port);
  const cacheKey = portName ?? '__default__';

  // Return cached output if available
  if (outputCache.has(cacheKey)) {
    return outputCache.get(cacheKey)!;
  }

  // Open and cache the output
  const output = portName ? midi.openMidiOut(portName) : midi.openMidiOut();
  outputCache.set(cacheKey, output);
  return output;
}

// ============================================================
// Core Functions
// ============================================================

/**
 * Calculate delay in milliseconds between now and when.
 *
 * @param now - Current timestamp in ms
 * @param when - Target timestamp in ms
 * @returns Delay in ms (negative if in the past)
 */
export function calculateMIDIDelayMS(now: number, when: number): number {
  return when - now;
}

/**
 * Normalize MIDI channel to the 0-15 range that JZZ expects.
 * Accepts 1-based channel numbers (1-16) or 0-based (0-15).
 *
 * @param channel - Channel number (1-based or 0-based)
 * @returns 0-based channel number (0-15)
 */
export function normalizeMidiChannel(channel: number | string | undefined | null): number {
  const n = Number(channel);
  if (!Number.isFinite(n)) return 0;
  // treat 1-16 as 1-based, otherwise assume already 0-based
  const zeroBased = n >= 1 && n <= 16 ? n - 1 : n;
  return Math.max(0, Math.min(15, Math.floor(zeroBased)));
}

/**
 * Schedule a MIDI note on a given output port and channel.
 *
 * @param port - Port name or number (numeric uses default)
 * @param channel - MIDI channel (1-16 or 0-15)
 * @param note - MIDI note number (0-127)
 * @param velocity - Note velocity (0-127)
 * @param when - Timestamp when to play (Date or ms)
 * @param durationMs - Note duration in milliseconds
 */
export async function scheduleMIDINote(
  port: number | string,
  channel: number | string,
  note: number,
  velocity: number,
  when: Date | number,
  durationMs: number = 100
): Promise<void> {
  let midiOutput: MIDIOutput;
  try {
    midiOutput = await getMidiOutput(port);
  } catch (e) {
    log.error('Failed to initialize JZZ/WebMIDI:', e as Error);
    return;
  }

  if (!midiOutput) {
    log.error('MIDI output not initialized. If you expect sound, ensure a synth/DAW is listening to your MIDI output.');
    return;
  }
  
  const whenMs = when instanceof Date ? when.getTime() : Number(when);
  const delay = calculateMIDIDelayMS(Date.now(), whenMs);
  
  if (delay > -100) {
    const ch = normalizeMidiChannel(channel);
    const lenMs = Number.isFinite(Number(durationMs)) ? Math.max(1, Math.round(Number(durationMs))) : 100;

    // Schedule Note On with cancelable timer
    const onTimer = setTimeout(() => {
      try {
        midiOutput.noteOn(ch, note, velocity);
      } catch (e) {
        log.error('Failed to send noteOn', e as Error);
      } finally {
        scheduledTimers.delete(onTimer);
      }
    }, Math.max(0, delay));
    scheduledTimers.add(onTimer);

    // Schedule Note Off
    const offTimer = setTimeout(() => {
      try {
        midiOutput.noteOff(ch, note);
      } catch (e) {
        log.error('Failed to send noteOff', e as Error);
      } finally {
        scheduledTimers.delete(offTimer);
      }
    }, Math.max(0, delay) + lenMs);
    scheduledTimers.add(offTimer);
  }
}

/**
 * Cancel all scheduled-but-not-yet-fired MIDI timers.
 */
export function clearScheduledNotes(): void {
  for (const t of scheduledTimers) {
    clearTimeout(t);
  }
  scheduledTimers.clear();
}

/**
 * Immediately send "All Sound Off" and "All Notes Off" on all channels.
 * This won't stop future scheduled events unless you also call clearScheduledNotes().
 *
 * @param port - Port name or number (numeric uses default)
 */
export async function panicAllNotesOff(port: number | string = 1): Promise<void> {
  let midi: JZZInstance;
  try {
    midi = await (JZZ() as unknown as Promise<JZZInstance>);
  } catch (e) {
    log.error('Failed to initialize JZZ/WebMIDI:', e as Error);
    return;
  }

  const portName = resolvePortName(midi, port);
  const midiOutput = portName ? midi.openMidiOut(portName) : midi.openMidiOut();

  if (!midiOutput) return;

  // CC 120: All Sound Off, CC 123: All Notes Off
  for (let ch = 0; ch < 16; ch++) {
    try {
      midiOutput.control(ch, 120, 0);
      midiOutput.control(ch, 123, 0);
    } catch {
      // Best-effort
    }
  }
}

/**
 * Manual test function for development.
 */
export async function test(): Promise<void> {
  try {
    const now = Date.now();
    const oneSecondFromNow = Date.now() + 1000;
    const twoSecondsFromNow = Date.now() + 2000;
    const threeSecondsFromNow = Date.now() + 3000;
    
    await scheduleMIDINote(1, 1, 69, 100, now, 100);
    await scheduleMIDINote(1, 1, 69, 100, oneSecondFromNow, 100);
    await scheduleMIDINote(1, 1, 69, 100, twoSecondsFromNow, 100);
    await scheduleMIDINote(1, 1, 69, 100, threeSecondsFromNow, 100);

    log.info('Notes scheduled', { now, oneSecondFromNow, twoSecondsFromNow, threeSecondsFromNow });
  } catch (error) {
    log.error('Failed to initialize JZZ:', error as Error);
  }
}

